/**
 * statBox
 *
 * Makes a whole stat box on a marketing stats page open the contacts behind
 * its number: the box is the target (click, Enter or Space), not just the
 * figure inside it. A box with nothing behind it (a zero) is left plain.
 *
 * Usage: `const box = statBox(open)` then
 * `<div className={cn('xyz-stat-card', box.className)} {...box.props}>`.
 */
import type { HTMLAttributes, KeyboardEvent } from 'react';

export interface StatBox {
  className: string;
  props: HTMLAttributes<HTMLDivElement>;
}

const CLICKABLE =
  'cursor-pointer transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400';

export function statBox(onOpen: (() => void) | null | undefined, title = 'View these contacts'): StatBox {
  if (!onOpen) return { className: '', props: {} };

  return {
    className: CLICKABLE,
    props: {
      role: 'button',
      tabIndex: 0,
      title,
      onClick: onOpen,
      onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      },
    },
  };
}

/** A contacts-page path with its filters, leaving out the empty ones. */
export function contactsPath(base: string, params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') qs.set(key, String(value));
  }
  const query = qs.toString();
  return query ? `${base}?${query}` : base;
}
